/**
 * Analytics tracking utilities
 * Centralizes Google Analytics and Meta Pixel event tracking
 */
import { isAnalyticsConsentGranted } from './components/ConsentBanner';

const analyticsAllowed = () => {
  if (process.env.NODE_ENV === 'test') return true;
  return isAnalyticsConsentGranted();
};

// Google Analytics Event Tracking
export const trackEvent = (eventName, parameters = {}) => {
  if (!analyticsAllowed()) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
    console.log('[Analytics] Event tracked:', eventName, parameters);
  }
};

// Meta Pixel Event Tracking
export const trackPixelEvent = (eventName, parameters = {}) => {
  if (!analyticsAllowed()) return;
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
    console.log('[Pixel] Event tracked:', eventName, parameters);
  }
};

// Combined tracking for convenience
export const track = (eventName, parameters = {}) => {
  trackEvent(eventName, parameters);
  trackPixelEvent(eventName, parameters);
};

const sanitize = (parameters) =>
  Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null)
  );

// Conversion Events
export const trackSignUp = (method = 'Email') => {
  track('sign_up', { method });
};

export const trackLogin = (method = 'Email') => {
  track('login', { method });
};

export const trackBeginCheckout = (plan, value) => {
  track('begin_checkout', {
    currency: 'USD',
    value: value,
    items: [{ 
      item_id: plan,
      item_name: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`,
      price: value
    }]
  });
};

export const trackPurchase = (transactionId, plan, value) => {
  track('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value: value,
    items: [{
      item_id: plan,
      item_name: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`,
      price: value
    }]
  });
};

export const trackAddVehicle = () => {
  track('add_vehicle', {});
};

export const trackAddMaintenance = () => {
  track('add_maintenance', {});
};

export const trackSetReminder = () => {
  track('set_reminder', {});
};

export const trackViewPricing = () => {
  track('view_pricing', {});
};

export const trackContactSubmit = () => {
  track('contact', { method: 'Contact Form' });
};

export const trackLandingNav = (target, audience) => {
  track('landing_nav_click', sanitize({ target, audience }));
};

export const trackLandingCta = ({
  action,
  placement,
  audience,
  plan,
  billingPeriod,
  promoActive,
}) => {
  track(
    'landing_cta_click',
    sanitize({
      action,
      placement,
      audience,
      plan,
      billing_period: billingPeriod,
      promo_active: promoActive,
    })
  );
};

export const trackLandingBillingToggle = (billingPeriod, audience) => {
  track('landing_billing_toggle', sanitize({ billing_period: billingPeriod, audience }));
};

export const trackLandingTemplateDownload = (assetType, audience) => {
  track('landing_template_download', sanitize({ asset_type: assetType, audience }));
};

export const trackCsvImport = (vehicleCount) => {
  track('csv_import', { vehicle_count: vehicleCount });
};

export const trackAiRecommendations = (vehicleId) => {
  track('view_ai_recommendations', { vehicle_id: vehicleId });
};

// Page view tracking (automatic with gtag, but can be called manually if needed)
export const trackPageView = (path) => {
  if (!analyticsAllowed()) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
      page_path: path
    });
  }
};
